// src/components/cards/ClientBookingsCard.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon } from '@mui/icons-material';
import { BookingListItem, bookingsAPI, Client } from '../../services/api';
import { StyledIconButton } from '../common/Buttons';
import ClientsBookingsList from '../lists/BookingsList/ClientsBookingsList';
import LoadingSpinner from '../common/LoadingSpinner';

type CardMode = 'bookings' | 'quotations';

interface ClientBookingsCardProps {
  clientId: number;
  client?: Client;
  onBookingClick?: (bookingId: number) => void;
  onCountReady?: (count: number) => void;
  // 'bookings' (default) loads everything except quotes; 'quotations' loads only quotes.
  mode?: CardMode;
}

const CardContainer = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.grey[900] : '#ffffff',
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '2rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  color: theme.palette.text.secondary,
  gap: '1.5rem',
}));

const BookingsListContainer = styled(Box)({
  marginTop: '8px',
  '& .MuiCard-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:hover': {
      transform: 'none',
      boxShadow: 'none',
    },
  },
});

const ClientBookingsCard: React.FC<ClientBookingsCardProps> = ({
  clientId,
  client,
  onBookingClick,
  onCountReady,
  mode = 'bookings',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bookingsLoadedRef = useRef(false);
  const lastModeRef = useRef<CardMode>(mode);
  const lastClientIdRef = useRef<number>(clientId);

  useEffect(() => {
    // Refetch when mode flips between bookings/quotations, or when the
    // client being viewed changes. Both matter: this card is mounted at a
    // fixed position in BeingsPage without a clientId-keyed `key` prop, so
    // navigating from one client's tab to another's (while staying on the
    // same tab) reuses this same component instance and only changes props
    // - without this check, bookingsLoadedRef would stay true and the card
    // would keep showing the previous client's bookings.
    if (lastModeRef.current !== mode || lastClientIdRef.current !== clientId) {
      bookingsLoadedRef.current = false;
      lastModeRef.current = mode;
      lastClientIdRef.current = clientId;
    }
    if (clientId && !bookingsLoadedRef.current) {
      setLoading(true);
      bookingsLoadedRef.current = true;

      const filters: { clientId: number; status?: string } = { clientId };
      if (mode === 'quotations') filters.status = 'Quotation';

      bookingsAPI
        .getAll(filters)
        .then((loadedBookings) => {
          const sorted = [...loadedBookings].sort((a, b) => b.id - a.id);
          setBookings(sorted);
          setError(null);
          onCountReady?.(sorted.length);
        })
        .catch(() => {
          setError(
            mode === 'quotations'
              ? 'Failed to load quotations'
              : 'Failed to load bookings'
          );
          setBookings([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [clientId, mode]);

  const handleEdit = (id: number) => {
    navigate(
      mode === 'quotations' ? `/quote/${id}/edit` : `/booking/${id}/edit`
    );
  };

  const handleView = (id: number) => {
    navigate(mode === 'quotations' ? `/quote/${id}` : `/booking/${id}`);
    if (onBookingClick) {
      onBookingClick(id);
    }
  };

  const handleNewBooking = () => {
    navigate(`/clients/${clientId}/newbooking`);
  };

  const handleNewQuotation = () => {
    navigate(`/clients/${clientId}/quote/new`);
  };

  const isQuotations = mode === 'quotations';
  const loadingMessage = isQuotations
    ? 'Loading quotations...'
    : 'Loading bookings...';
  const emptyHeading = isQuotations ? '0 Quotations found' : '0 Bookings found';
  const emptyCta = isQuotations
    ? `Add a quotation for ${client?.firstName ?? 'this client'}`
    : `Add a booking for ${client?.firstName ?? 'this client'}`;
  const emptyTitle = isQuotations ? 'Add new quotation' : 'Add new booking';
  const onAddClick = isQuotations ? handleNewQuotation : handleNewBooking;

  if (loading) {
    return (
      <CardContainer>
        <LoadingSpinner message={loadingMessage} size={40} />
      </CardContainer>
    );
  }

  if (error) {
    return (
      <CardContainer>
        <EmptyState>
          <Typography variant="body1" color="error">
            {error}
          </Typography>
        </EmptyState>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      {bookings.length === 0 ? (
        <EmptyState>
          <Typography
            variant="h4"
            sx={{ fontSize: '2rem', color: `${theme.palette.primary.main}` }}
          >
            {emptyHeading}
          </Typography>
          <Typography variant="h4" sx={{ fontSize: '2rem' }} gutterBottom>
            {emptyCta}
          </Typography>
          <StyledIconButton
            onClick={onAddClick}
            variant="delete"
            sx={{
              width: '3.5rem',
              height: '3.5rem',
              backgroundColor: theme.palette.secondary.main,
              '& svg': {
                color: '#fff',
                fontSize: '3rem',
              },
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
              },
            }}
            title={emptyTitle}
            aria-label={emptyTitle}
          >
            <AddIcon />
          </StyledIconButton>
        </EmptyState>
      ) : (
        <BookingsListContainer>
          <ClientsBookingsList
            items={bookings}
            type="bookings"
            onEdit={handleEdit}
            onView={handleView}
          />
        </BookingsListContainer>
      )}
    </CardContainer>
  );
};

export default ClientBookingsCard;
